import { resolveId } from '@medplum/core'
import type { Composition } from '@medplum/fhirtypes'
import { hasCodeableConcept } from '#src/codeableConcept.ts'
import { Assessment } from '#src/models/assessments/assessment.ts'
import { CarePlanSnapshot } from '#src/models/care-plans/CarePlanSnapshot.ts'
import { CompositionNote } from '#src/models/compositionNote.ts'
import { CompositionCategory } from '#src/models/compositions/constants.ts'
import { VisitNote } from '#src/models/visitNote.ts'

/* A Composition is a chart note, an assessment, an incident, or a care plan
 * snapshot, and
 * each lives on a different page under a different route. Route by the
 * resource's actual kind rather than guessing.
 *
 * In core rather than beside the mention chip that first needed it, because an
 * AI tool now builds the same link when it withholds an unsigned note's
 * content, and a second path literal would rot the first time a route moved. */
export function compositionUrl(composition: Composition): string | null {
  const participantId = resolveId(composition.subject)
  const id = composition.id
  if (!participantId || !id) {
    return null
  }
  /* Matched by category, and ahead of the assessment branch it would otherwise
   * fall past: an incident's `type` carries the incident level rather than an
   * assessment type, so `Assessment.isInstance` misses it even though an
   * incident is written and signed like any other assessment. Without this it
   * fell through every branch and got no link at all. */
  if (hasCodeableConcept(composition.category, CompositionCategory.Incident)) {
    return `/Patient/${participantId}/incidents/${id}`
  }
  if (Assessment.isInstance(composition)) {
    return `/Patient/${participantId}/assessments/${id}`
  }
  /* Both kinds of note share the one detail page. An Elation note is matched by
   * its template type rather than the Note category, which it does not carry —
   * without this branch it fell through and got no link at all. */
  if (
    CompositionNote.isInstance(composition) ||
    VisitNote.isInstance(composition)
  ) {
    return `/Patient/${participantId}/visit-notes/${id}`
  }
  if (CarePlanSnapshot.isInstance(composition)) {
    return `/Patient/${participantId}/care-plans/history/${id}`
  }
  return null
}
